import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "@/locales/en/common.json";
import enDashboard from "@/locales/en/dashboard.json";
import enOrders from "@/locales/en/orders.json";
import enMenu from "@/locales/en/menu.json";
import enKitchen from "@/locales/en/kitchen.json";
import enPayments from "@/locales/en/payments.json";
import enAuth from "@/locales/en/auth.json";
import enSettings from "@/locales/en/settings.json";
import enCustomers from "@/locales/en/customers.json";
import enEvents from "@/locales/en/events.json";
import enPublic from "@/locales/en/public.json";
import enTraining from "@/locales/en/training.json";
import enPayrollBridge from "@/locales/en/payrollBridge.json";
import enHq from "@/locales/en/hq.json";
import enAutopilot from "@/locales/en/autopilot.json";
import enAiStaff from "@/locales/en/aiStaff.json";
import esCommon from "@/locales/es/common.json";
import esDashboard from "@/locales/es/dashboard.json";
import esOrders from "@/locales/es/orders.json";
import esMenu from "@/locales/es/menu.json";
import esKitchen from "@/locales/es/kitchen.json";
import esPayments from "@/locales/es/payments.json";
import esAuth from "@/locales/es/auth.json";
import esSettings from "@/locales/es/settings.json";
import esCustomers from "@/locales/es/customers.json";
import esEvents from "@/locales/es/events.json";
import esPublic from "@/locales/es/public.json";
import esTraining from "@/locales/es/training.json";
import esPayrollBridge from "@/locales/es/payrollBridge.json";
import esHq from "@/locales/es/hq.json";
import esAutopilot from "@/locales/es/autopilot.json";
import esAiStaff from "@/locales/es/aiStaff.json";

export const SUPPORTED_LANGUAGES = ["en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const NAMESPACES = [
  "common",
  "dashboard",
  "orders",
  "menu",
  "kitchen",
  "payments",
  "auth",
  "settings",
  "customers",
  "events",
  "public",
  "training",
  "payrollBridge",
  "hq",
  "autopilot",
  "aiStaff",
] as const;
export type AppNamespace = (typeof NAMESPACES)[number];

export const defaultNS = "common" as const;

export const resources = {
  en: {
    common: enCommon,
    dashboard: enDashboard,
    orders: enOrders,
    menu: enMenu,
    kitchen: enKitchen,
    payments: enPayments,
    auth: enAuth,
    settings: enSettings,
    customers: enCustomers,
    events: enEvents,
    public: enPublic,
    training: enTraining,
    payrollBridge: enPayrollBridge,
    hq: enHq,
    autopilot: enAutopilot,
    aiStaff: enAiStaff,
  },
  es: {
    common: esCommon,
    dashboard: esDashboard,
    orders: esOrders,
    menu: esMenu,
    kitchen: esKitchen,
    payments: esPayments,
    auth: esAuth,
    settings: esSettings,
    customers: esCustomers,
    events: esEvents,
    public: esPublic,
    training: esTraining,
    payrollBridge: esPayrollBridge,
    hq: esHq,
    autopilot: esAutopilot,
    aiStaff: esAiStaff,
  },
} as const satisfies Record<SupportedLanguage, Record<AppNamespace, unknown>>;

const missingNamespaces = SUPPORTED_LANGUAGES
  .map((language) => {
    const resourceNamespaces = Object.keys(resources[language]);
    return NAMESPACES.filter((namespace) => !resourceNamespaces.includes(namespace));
  })
  .flat();

const isDevEnvironment = import.meta.env.MODE === 'development';

if (missingNamespaces.length > 0 && isDevEnvironment) {
  console.warn(
    `Missing i18n namespaces detected: ${missingNamespaces.join(", ")}. Check locale registration.`,
  );
}

if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      resources,
      lng: "en",
      fallbackLng: "en",
      defaultNS,
      ns: [...NAMESPACES],
      interpolation: {
        escapeValue: false,
      },
      returnNull: false,
    })
    .catch((error) => {
      if (isDevEnvironment) {
        console.error("Failed to initialize i18n", error);
      }
    });
}

export default i18n;
